import i18n from '@/localisation/i18n';
import { SidebarGroupChildren, SidebarItem } from '@/modules/sidebar/types/sidebar';
import { Role } from '@/utils/helpers/permissions';
import { ClientConfig } from '@/utils/helpers/rest';
import { TradingPermission, hasOneOfTradingPermission } from '@/utils/helpers/trading-permissions';
import { LoginState } from '@/store/store';

/*
 * 1) Hydrate isVisible
 * 2) Filter by isVisible and user role
 * 3) Return the items for the Sidebar
 */
export function getSidebarItems(
  clientCfg: ClientConfig,
  ls: LoginState,
  notificationsCounts: Record<string, number>
): SidebarItem[] {
  const items: SidebarItem[] = [
    // Marketplace
    {
      kind: 'group',
      key: 'marketplace',
      children: [
        {
          to: 'marketplace.orderbook',
          title: i18n.t('sidebar.items.marketplaceOrderbook'),
          isVisible: clientCfg.marketplaceTopOfBookEnabled,
        },
        {
          to: 'marketplace.symbol-overview',
          title: i18n.t('sidebar.items.marketplaceSymbolOverview'),
          isVisible: clientCfg.marketplaceTopOfBookEnabled,
        },
        {
          to: 'marketplace.explorer',
          title:
            clientCfg.marketplaceTopOfBookEnabled && clientCfg.marketplaceExplorerEnabled
              ? i18n.t('sidebar.items.marketplaceExplorer')
              : i18n.t('sidebar.items.marketplaceOrderbook'),
          isVisible: clientCfg.marketplaceExplorerEnabled,
        },
        { to: 'marketplace.orders', title: i18n.t('sidebar.items.marketplaceOrders') },
        {
          to: 'marketplace.executions',
          title: i18n.t('sidebar.items.marketplaceExecutions'),
          notificationsColor: 'deep-orange',
          notificationsCount: notificationsCounts.executions,
        },
        { to: 'marketplace.watchlists', title: i18n.t('sidebar.items.marketplaceWatchlists') },
      ],
      title: i18n.t('sidebar.items.marketplace'),
      icon: 'mdi-chart-line-variant',
      role: Role.TraderViewer,
      isVisible:
        hasOneOfTradingPermission(ls, TradingPermission.LenderOrBorrowerPermissions) &&
        ls.user?.sponsorshipSide !== 'sponsored' &&
        !ls.user?.roles?.includes('ops-user'),
    },
    // Loan Manager
    {
      kind: 'group',
      key: 'loan-manager',
      children: [
        {
          to: 'loans.explorer',
          title: i18n.t('sidebar.items.loansExplorer'),
          isVisible:
            hasOneOfTradingPermission(ls, TradingPermission.LenderOrBorrowerPermissions) &&
            ls.user?.sponsorshipSide !== 'sponsored',
        },
        {
          to: 'loans.open',
          title: i18n.t('sidebar.items.openLoans'),
          isVisible:
            hasOneOfTradingPermission(ls, TradingPermission.LenderOrBorrowerPermissions) &&
            ls.user?.sponsorshipSide !== 'sponsored',
        },
        {
          to: 'loans.history',
          title: i18n.t('sidebar.items.loansHistory'),
          isVisible:
            hasOneOfTradingPermission(ls, TradingPermission.LenderOrBorrowerPermissions) &&
            ls.user?.sponsorshipSide !== 'sponsored',
        },
        {
          to: 'loans.manual',
          title: i18n.t('sidebar.items.manualLoans'),
          isVisible:
            hasOneOfTradingPermission(ls, TradingPermission.LenderOrBorrowerPermissions) &&
            ls.user?.sponsorshipSide !== 'sponsored' &&
            !ls.user?.roles?.includes('ops-user'),
        },
        {
          to: 'loans.pre-established',
          title: i18n.t('sidebar.items.preEstablished'),
          isVisible:
            hasOneOfTradingPermission(ls, TradingPermission.LenderOrBorrowerPermissions) &&
            ls.user?.sponsorshipSide !== 'sponsored' &&
            !ls.user?.roles?.includes('ops-user'),
        },
        {
          to: 'sponsorship.loans',
          title: i18n.t('sidebar.items.sponsoredLoans'),
          isVisible: ls.user?.sponsorshipSide !== null,
        },

        {
          to: 'termloans.contracts',
          title: i18n.t('sidebar.items.termLoans'),
          isVisible:
            clientCfg.termLoansEnabled &&
            hasOneOfTradingPermission(ls, TradingPermission.LenderOrBorrowerPermissions) &&
            ls.user?.sponsorshipSide !== 'sponsored',
        },
      ],
      title: i18n.t('sidebar.items.loanManager'),
      icon: 'mdi-compass',
      role: Role.TraderViewer,
    },
    {
      kind: 'link',
      key: 'agreements',
      to: 'agreements',
      title: i18n.t('sidebar.items.agreements'),
      icon: 'mdi-swap-horizontal',
      role: Role.TraderViewer,
      isVisible:
        hasOneOfTradingPermission(ls, TradingPermission.LenderOrBorrowerPermissions) &&
        ls.user?.sponsorshipSide !== 'sponsored' &&
        !ls.user?.roles?.includes('ops-user'),
    },
    // Trader user
    {
      kind: 'link',
      key: 'notrading',
      to: 'notrading',
      title: i18n.t('sidebar.items.notrading'),
      icon: 'mdi-account-alert',
      role: Role.TraderViewer,
      isVisible: !hasOneOfTradingPermission(ls, TradingPermission.LenderOrBorrowerPermissions),
    },
    {
      kind: 'link',
      key: 'borrower.locates',
      to: 'borrower.locates',
      title: i18n.t('sidebar.items.borrowerLocates'),
      icon: 'mdi-card-search-outline',
      role: Role.TraderViewer,
      isVisible:
        clientCfg.demoMode &&
        hasOneOfTradingPermission(ls, TradingPermission.BorrowerPermissions) &&
        ls.user?.sponsorshipSide !== 'sponsored' &&
        !ls.user?.roles?.includes('ops-user'),
    },

    {
      kind: 'group',
      key: 'analytics',
      children: [
        {
          to: 'analytics.dashboard',
          title: i18n.t('sidebar.items.dashboard'),
          isVisible: clientCfg.nsccDashboardEnabled,
        },
        {
          to: 'analytics.varCalculator',
          title: i18n.t('sidebar.items.varCalculator'),
          isVisible: clientCfg.varCalcEnabled,
        },
        {
          to: 'analytics.trading',
          title: i18n.t('sidebar.items.tradingAnalytics'),
          isVisible: clientCfg.seclendingAnalyticsEnabled,
        },
      ],
      title: i18n.t('sidebar.items.analytics'),
      icon: 'mdi-google-analytics',
      role: Role.TraderViewer,
      isVisible:
        (clientCfg.nsccDashboardEnabled ||
          clientCfg.varCalcEnabled ||
          clientCfg.seclendingAnalyticsEnabled) &&
        ls.user?.sponsorshipSide !== 'sponsored' &&
        hasOneOfTradingPermission(ls, TradingPermission.LenderOrBorrowerPermissions),
    },
    {
      kind: 'link',
      key: 'reports',
      to: 'reports',
      title: i18n.t('sidebar.items.reports'),
      icon: 'mdi-chart-timeline',
      role: Role.TraderViewer,
      isVisible: hasOneOfTradingPermission(ls, TradingPermission.LenderOrBorrowerPermissions),
    },
    {
      kind: 'link',
      key: 'transactions',
      to: 'transactions',
      title: i18n.t('sidebar.items.reportingFacility'),
      icon: 'mdi-chart-tree',
      role: Role.TraderViewer,
      isVisible: clientCfg.finraTransactionsEnabled,
    },
    {
      kind: 'link',
      key: 'swaps',
      // route name must exist in routes.ts, or vue-router consoles a warning
      to: 'error-400',
      // route name doesn't matter since this item isDisabled (displayed only, no link)
      isDisabled: true,
      title: i18n.t('sidebar.items.swaps'),
      icon: 'mdi-swap-horizontal-variant',
      role: Role.TraderViewer,
      isVisible: clientCfg.demoMode,
    },
    {
      kind: 'link',
      key: 'trade-optimizer',
      // route name must exist in routes.ts, or vue-router consoles a warning
      to: 'error-400',
      // route name doesn't matter since this item isDisabled (displayed only, no link)
      isDisabled: true,
      title: i18n.t('sidebar.items.tradeOptimizer'),
      icon: 'mdi-shape-circle-plus',
      role: Role.TraderViewer,
      isVisible: clientCfg.demoMode,
    },
    {
      kind: 'link',
      key: 'basket-tool',
      // route name must exist in routes.ts, or vue-router consoles a warning
      to: 'error-400',
      // route name doesn't matter since this item isDisabled (displayed only, no link)
      isDisabled: true,
      title: i18n.t('sidebar.items.basketTool'),
      icon: 'mdi-basket',
      role: Role.TraderViewer,
      isVisible: clientCfg.demoMode,
    },

    // Trader admin
    {
      kind: 'group',
      key: 'trader-admin',
      children: [
        {
          to: 'trader-admin.users',
          title: i18n.t('sidebar.items.traderAdminUsers'),
        },
        {
          to: 'trader-admin.company-preferences',
          title: i18n.t('sidebar.items.traderAdminCompanyPreferences'),
        },
        {
          to: 'trader-admin.counterparty-credit',
          title: i18n.t('sidebar.items.traderAdminCounterpartyCredit'),
        },
        {
          to: 'trader-admin.uptime-statistics',
          title: i18n.t('sidebar.items.adminUptimeStatistics'),
        },
        {
          to: 'trader-admin.integrations',
          title: i18n.t('sidebar.items.traderAdminIntegrations'),
        },
      ],
      title: i18n.t('sidebar.items.traderAdmin'),
      icon: 'mdi-account-details',
      role: Role.TraderAdmin,
      isVisible: true,
    },

    // Broker user
    {
      kind: 'group',
      key: 'broker-admin.accounts',
      children: [
        {
          to: 'broker-admin.accounts.users',
          title: i18n.t('sidebar.items.brokerAdminAccountsUsers'),
        },
        {
          to: 'broker-admin.accounts.companies',
          title: i18n.t('sidebar.items.brokerAdminAccountsCompanies'),
        },
        {
          to: 'broker-admin.accounts.profiles',
          title: i18n.t('sidebar.items.brokerAdminAccountsProfiles'),
        },
        {
          to: 'broker-admin.accounts.ipblacklist',
          title: i18n.t('sidebar.items.brokerAdminAccountsIpblacklist'),
        },
      ],
      title: i18n.t('sidebar.items.brokerAdminAccounts'),
      icon: 'mdi-account-details',
      role: Role.BrokerAdmin,
      isVisible: true,
    },
    {
      kind: 'link',
      key: 'broker-admin.tables.equities',
      to: 'broker-admin.tables.equities',
      title: i18n.t('sidebar.items.equities'),
      icon: 'mdi-database',
      role: Role.BrokerAdmin,
      isVisible: true,
    },
    {
      kind: 'link',
      key: 'broker-admin.kill-switch',
      to: 'broker-admin.kill-switch',
      title: i18n.t('sidebar.items.killSwitch'),
      icon: 'mdi-toggle-switch-off',
      role: Role.BrokerComplianceRep,
      isVisible: true,
    },
    {
      kind: 'link',
      key: 'broker-user.uptime-statistics',
      to: 'broker-user.uptime-statistics',
      title: i18n.t('sidebar.items.adminUptimeStatistics'),
      icon: 'mdi-chart-areaspline',
      role: Role.BrokerUser,
      isVisible: true,
    },
    {
      kind: 'link',
      key: 'broker-user.stats',
      to: 'broker-user.stats',
      title: i18n.t('sidebar.items.stats'),
      icon: 'mdi-chart-areaspline',
      role: Role.BrokerUser,
      isVisible: true,
    },
    {
      kind: 'group',
      key: 'broker-user.client-monitor',
      children: [
        {
          to: 'broker-user.client-monitor.marketplace-orders',
          title: i18n.t('sidebar.items.clientMonitorMarketplaceOrders'),
        },
        {
          to: 'broker-user.client-monitor.marketplace-executions',
          title: i18n.t('sidebar.items.clientMonitorMarketplaceExecutions'),
        },
        {
          to: 'broker-user.client-monitor.marketplace-explorer',
          title: i18n.t('sidebar.items.clientMonitorMarketplaceExplorer'),
          isVisible: clientCfg.marketplaceExplorerEnabled,
        },
        {
          to: 'broker-user.client-monitor.marketplace-orderbook',
          title: i18n.t('sidebar.items.clientMonitorMarketplaceOrderbook'),
        },
        {
          to: 'broker-user.client-monitor.marketplace-depth-of-book',
          title: i18n.t('sidebar.items.clientMonitorMarketplaceDepthOfBook'),
        },
        {
          to: 'broker-user.client-monitor.agreements',
          title: i18n.t('sidebar.items.clientMonitorAgreements'),
        },
        {
          to: 'broker-user.client-monitor.open-loans',
          title: i18n.t('sidebar.items.clientMonitorOpenLoans'),
        },
        {
          to: 'broker-user.client-monitor.loans-history',
          title: i18n.t('sidebar.items.clientMonitorLoansHistory'),
        },
        {
          to: 'broker-user.client-monitor.reports',
          title: i18n.t('sidebar.items.clientMonitorReports'),
        },
        {
          to: 'broker-user.client-monitor.reporting',
          title: 'Report Delivery',
        },
      ],
      title: i18n.t('sidebar.items.clientMonitor') as string,
      icon: 'mdi-face-agent',
      role: Role.BrokerUser,
      isVisible: true,
    },
  ];

  return items
    .filter((item) => {
      // don't display any item which has item.isVisible set to false
      return (item.isVisible ?? true) && ls.permissionValidator?.hasPermission(item.role);
    })
    .map((item: SidebarItem): SidebarItem => {
      if (item.kind === 'group') {
        return {
          ...item,
          // don't display any children which have c.isVisible set to false
          children: item.children.filter((c) => c.isVisible ?? true) as SidebarGroupChildren,
        };
      }
      return item;
    });
}
